/**
 * Router — lazy-loaded routes with role-based access control.
 *
 * Each page is a separate chunk that only downloads when navigated to.
 * Unauthorized users are redirected before the chunk loads.
 */

import { Component, lazy, Suspense, type ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router';
import { useAuth, type User } from './providers/AuthProvider';
import { isChunkLoadError, reloadOnceForChunkError } from './lib/chunk-reload';
import { hasMinRole } from './lib/roles';
import { hasPermission } from '../lib/permissions';

// ── Role helpers ────────────────────────────────────────────────────────────────────────────────
// Role hierarchy + `hasMinRole` live in ./lib/roles (shared with contact-card surfaces).

function isPlatformOps(user: User | null): boolean {
  return user?.platformOps === true;
}

/**
 * Portal-only: the user's only role is agency_owner. Server-computed flag
 * (see /auth/* userinfo cookie) — `role` reads 'rep' for these users, so
 * the flag is the only reliable client-side signal. The shell collapses
 * to the AO portal for them.
 */
function isPortalOnly(user: User | null): boolean {
  return user?.portalOnly === true;
}

/**
 * Who can open the AO portal: agency owners, and internal team —
 * platform-ops or any @apexpro.ai account. Mirrors the server's portal
 * session check (isPortalAdmin + agency_owner realm role in
 * src/services/ao-auth.ts).
 */
function canSeePortal(user: User | null): boolean {
  if (!user) return false;
  if (user.agencyOwner === true || user.platformOps === true) return true;
  return user.email.endsWith('@apexpro.ai');
}

// ── Lazy page imports — each becomes its own chunk ──────────────────────────────────────────────

const InboxPage = lazy(() => import('./pages/InboxPage').then((m) => ({ default: m.InboxPage })));
const QueueConsolePage = lazy(() =>
  import('./pages/QueueConsolePage').then((m) => ({ default: m.QueueConsolePage })),
);
const ContactsPage = lazy(() =>
  import('./pages/ContactsPage').then((m) => ({ default: m.ContactsPage })),
);
const AOPortalPage = lazy(() =>
  import('./pages/AOPortalPage').then((m) => ({ default: m.AOPortalPage })),
);
const ReportingPage = lazy(() =>
  import('./pages/ReportingPage').then((m) => ({ default: m.ReportingPage })),
);
const DashboardPage = lazy(() =>
  import('./pages/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
const AdminConsolePage = lazy(() =>
  import('./pages/AdminConsolePage').then((m) => ({ default: m.AdminConsolePage })),
);
const DevSupportPage = lazy(() =>
  import('./pages/DevSupportPage').then((m) => ({ default: m.DevSupportPage })),
);
// ── Loading fallback ────────────────────────────────────────────────────────────────────────────

function PageLoading() {
  return (
    <div className="flex-1 flex items-center justify-center">
      <div className="flex items-center gap-3 text-muted-foreground">
        <div className="w-5 h-5 border-2 border-border border-t-primary rounded-full animate-spin" />
        <span className="text-sm">Loading...</span>
      </div>
    </div>
  );
}

// ── Route guard ─────────────────────────────────────────────────────────────────────────────────

function RequireRole({
  minRole,
  agencyOwnerOnly,
  children,
}: {
  minRole?: string;
  agencyOwnerOnly?: boolean;
  children: React.ReactNode;
}) {
  const { user, isLoading, effectiveRole, isImpersonating } = useAuth();

  // Wait for auth to finish before making access decisions
  if (isLoading) return <PageLoading />;

  // Platform-ops bypasses all role checks — unless impersonating, where
  // the whole point is to see exactly what the effective role sees, so
  // the guards enforce the assumed role instead.
  if (isPlatformOps(user) && !isImpersonating) return <>{children}</>;

  const role = isImpersonating ? effectiveRole : user?.role || 'rep';

  // Agency owner portal route — owners + internal team. When impersonating,
  // only the agency_owner view keeps the portal.
  const portalAllowed = isImpersonating ? effectiveRole === 'agency_owner' : canSeePortal(user);
  if (agencyOwnerOnly && !portalAllowed) {
    return <Navigate to="/inbox" replace />;
  }

  // Role check
  if (minRole && !hasMinRole(role, minRole)) {
    return <Navigate to="/inbox" replace />;
  }

  return <>{children}</>;
}

// ── Route config (exported for Sidebar to filter) ────────────────────────────

export interface RouteConfig {
  path: string;
  label: string;
  icon: string; // lucide icon name
  minRole?: string;
  agencyOwnerOnly?: boolean;
  /**
   * Server-resolved permission required to see this route (on top of the
   * role checks). Resolved per effective role by /auth/permissions, so it
   * also honors impersonation and per-user permission overrides. Roles
   * with an empty permission set (agency_owner) see nothing but the portal.
   */
  permission?: string;
}

export const ROUTE_CONFIG: RouteConfig[] = [
  { path: '/inbox', label: 'Inbox', icon: 'MessageSquare', permission: 'inbox.view' },
  { path: '/contacts', label: 'Contacts', icon: 'Users', permission: 'contacts.view' },
  { path: '/console', label: 'Queue Console', icon: 'Phone', permission: 'console.view' },
  { path: '/dashboard', label: 'Dashboard', icon: 'Activity', permission: 'dashboard.view' },
  { path: '/ao-portal', label: 'Agency Owner Portal', icon: 'Star', agencyOwnerOnly: true },
  {
    path: '/reporting',
    label: 'Reporting',
    icon: 'BarChart3',
    minRole: 'supervisor',
    permission: 'reporting.view',
  },
  {
    path: '/admin',
    label: 'Admin Console',
    icon: 'Settings',
    minRole: 'admin',
    permission: 'admin.system.view',
  },
];

/**
 * Check if the current user can access a route.
 */
export function canAccessRoute(user: User | null, route: RouteConfig): boolean {
  if (!user) return false;
  if (isPlatformOps(user)) return true;
  // Portal-only users get exactly the portal — nothing else.
  if (isPortalOnly(user)) return route.agencyOwnerOnly === true;
  if (route.agencyOwnerOnly && !canSeePortal(user)) return false;
  if (route.minRole && !hasMinRole(user.role, route.minRole)) return false;
  if (route.permission && !hasPermission(route.permission)) return false;
  return true;
}

/**
 * Check whether a specific `role` (not necessarily the caller's real role)
 * could access this route. Used while impersonating: the sidebar filters
 * by the effective role so a platform-ops user sees exactly the nav that
 * role would see — no platform-ops short-circuit, no real-identity portal
 * access. The portal route belongs to agency_owner alone here.
 */
export function canAccessRouteAsRole(user: User | null, route: RouteConfig, role: string): boolean {
  if (!user) return false;
  if (route.agencyOwnerOnly) return role === 'agency_owner';
  if (route.minRole && !hasMinRole(role, route.minRole)) return false;
  // While impersonating, /auth/permissions resolves against the effective
  // role, so this check reflects the assumed role's permissions too.
  if (route.permission && !hasPermission(route.permission)) return false;
  return true;
}

// ── Lazy-route error boundary ─────────────────────────────────────────────────────────────────

/**
 * Catches errors thrown while rendering the lazy routes. Its job is the
 * rollout safety net (#1060): when an old-build tab navigates into a route
 * whose hashed chunk no longer exists on the pod it hit, the dynamic
 * `import()` rejects and React surfaces it here. We trigger a single guarded
 * reload (loop-guarded in chunk-reload.ts) onto the now-consistent build.
 *
 * Non-chunk render errors are rethrown so they aren't silently swallowed —
 * this boundary only owns the chunk-load case.
 */
class LazyRouteErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError(error: unknown) {
    if (isChunkLoadError(error)) return { failed: true };
    // Not a chunk error — let it propagate to a higher boundary.
    throw error;
  }

  componentDidCatch() {
    // Attempt the one-time reload. If the loop-guard suppresses it (we already
    // reloaded in this window), we fall through to the inert fallback below
    // rather than looping — a manual refresh, not a white-screen loop.
    reloadOnceForChunkError();
  }

  render() {
    if (this.state.failed) {
      // Shown only in the rare case the reload was loop-guard-suppressed.
      return <PageLoading />;
    }
    return this.props.children;
  }
}

// ── App Routes ──────────────────────────────────────────────────────────────────────────────────

export function AppRoutes() {
  const { portalView } = useAuth();

  // Portal-only shell: the AO portal is the entire app. Every other path
  // lands back on it — including old bookmarks into /inbox etc. AppShell
  // only mounts routes after auth resolves, so `portalView` is final here.
  // Covers real portal-only owners AND platform-ops impersonating one.
  // Feedback & Support is the one shared page: same DevSupportPage as the
  // main app, server-scoped to the client tier (own org, client_facing).
  if (portalView) {
    return (
      <LazyRouteErrorBoundary>
        <Suspense fallback={<PageLoading />}>
          <Routes>
            <Route path="/ao-portal" element={<AOPortalPage />} />
            <Route path="/dev-support" element={<DevSupportPage />} />
            <Route path="*" element={<Navigate to="/ao-portal" replace />} />
          </Routes>
        </Suspense>
      </LazyRouteErrorBoundary>
    );
  }

  return (
    <LazyRouteErrorBoundary>
      <Suspense fallback={<PageLoading />}>
        <Routes>
          <Route path="/inbox" element={<InboxPage />} />
          <Route path="/contacts" element={<ContactsPage />} />
          <Route path="/contacts/:contactId" element={<ContactsPage />} />
          <Route path="/console" element={<QueueConsolePage />} />
          <Route path="/dev-support" element={<DevSupportPage />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route
            path="/ao-portal"
            element={
              <RequireRole agencyOwnerOnly>
                <AOPortalPage />
              </RequireRole>
            }
          />
          <Route
            path="/reporting"
            element={
              <RequireRole minRole="supervisor">
                <ReportingPage />
              </RequireRole>
            }
          />
          <Route
            path="/admin"
            element={
              <RequireRole minRole="admin">
                <AdminConsolePage />
              </RequireRole>
            }
          />
          <Route
            path="/admin/:tab"
            element={
              <RequireRole minRole="admin">
                <AdminConsolePage />
              </RequireRole>
            }
          />
          {/* Default redirect */}
          <Route path="*" element={<Navigate to="/inbox" replace />} />
        </Routes>
      </Suspense>
    </LazyRouteErrorBoundary>
  );
}

export { BrowserRouter };
